import type React from 'react';
import { type PropsWithChildren, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { useNavigation } from '@/hooks/navigation';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { useScrollBlock } from '@/modules/common/hooks/useScrollBlock';
import {
  MissionMode,
  type QuestMissionWithMode,
} from '@/modules/promotions/helpers';
import { ScTooltip, ScTooltipMobile } from './styled';

type MissionTooltipProps = Omit<
  QuestMissionWithMode,
  'vipMission' | 'vipPrize' | 'prize' | 'mission'
> & {
  mission: string;
  onClose: () => void;
};

export const MissionTooltip: React.FC<MissionTooltipProps> = ({
  mode,
  title,
  mission,
  type,
  prizeType,
  date,
  terms,
  prizeUrl,
  missionUrl,
  completeImg,
  isSpecialMission,
  missionExternalUrls,
  onClose,
}) => {
  const { t } = useTranslation('quest');
  const { navigate } = useNavigation();
  const tooltipRef = useRef<HTMLDivElement>(null);

  const isCompleted = useMemo(
    () =>
      mode === MissionMode.TodayCompleted || mode === MissionMode.PastCompleted,
    [mode]
  );

  const tooltipContent = useMemo(() => {
    if (isSpecialMission) {
      if (isCompleted) {
        return (
          <div className="content smallGap">
            <p className="title">{t('tooltip.cash.title')}</p>
            <p className="body">{t('tooltip.cash.body')}</p>
          </div>
        );
      }

      return (
        <div className="content smallGap">
          <ImageComponent
            alt="tooltip image"
            height={64}
            src={'/quest-xmas/tooltip-special.webp'}
            width={64}
          />
          <p className="title">{t('tooltip.specialMission.title')}</p>
          <p className="body">{t(mission)}</p>
          <p className="terms">{t(terms)}</p>
        </div>
      );
    }

    switch (mode) {
      case MissionMode.TodayActive:
        return (
          <div className="content">
            <ImageComponent
              alt="tooltip image"
              className="activeImg"
              height={48}
              src={completeImg}
              width={48}
            />
            <p className="title">{t(title)}</p>
            <p className="body">{t(mission)}</p>
            <p className="terms">{t(terms)}</p>
          </div>
        );
      case MissionMode.TodayCompleted:
      case MissionMode.PastCompleted:
        return (
          <div className="content smallGap">
            {prizeType === 'lootbox' && (
              <ImageComponent
                alt="tooltip image"
                className="image"
                height={64}
                src={'/quest-xmas/tooltip-lootbox.webp'}
                width={64}
              />
            )}
            <p className="title">{t(`tooltip.${prizeType}.title`)}</p>
            <p className="body">{t(`tooltip.${prizeType}.body`)}</p>
          </div>
        );
      case MissionMode.Future:
        return (
          <div className="content">
            <p className="title">{t('tooltip.future.title')}</p>
            <p className="body">{t('tooltip.future.body', { date })}</p>
          </div>
        );
      case MissionMode.PastFailed:
      case MissionMode.TodayFailed:
        return (
          <div className="content">
            <p className="largeTitle">{t('tooltip.failed.title')}</p>
          </div>
        );
    }
  }, [
    mission,
    mode,
    t,
    title,
    date,
    completeImg,
    prizeType,
    terms,
    isSpecialMission,
    isCompleted,
  ]);

  const ctaButtonText = useMemo(() => {
    if (prizeType === 'tg/wa' || isSpecialMission) return;

    if (mode === MissionMode.TodayActive) {
      return t(`tooltip.${type}`);
    }

    if (prizeType === 'fs' && isCompleted) {
      return t('tooltip.activateFs');
    }
  }, [mode, t, type, prizeType, isCompleted, isSpecialMission]);

  const handleClickCta = () => {
    if (prizeUrl && prizeType === 'fs' && isCompleted) {
      return navigate(prizeUrl);
    }

    if (!missionUrl) return;

    navigate(missionUrl);
  };

  const handleClickTelegramOrWhatsapp = (url?: string) => () => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useOnClickOutside(tooltipRef, onClose);

  return (
    <MobileTooltip>
      <ScTooltip ref={tooltipRef}>
        <div className="contentContainer">
          <ButtonClose
            className="closeBtn"
            onClick={onClose}
            right={4}
            top={4}
          />
          <ImageComponent
            alt="tooltip background"
            className="background"
            height={471}
            src={'/quest-xmas/tooltip-bg2.webp'}
            width={358}
          />
          {tooltipContent}
          {ctaButtonText && (
            <div className="ctaButton">
              <Button large onClick={handleClickCta} primary wide>
                {ctaButtonText}
              </Button>
            </div>
          )}
          {prizeType === 'tg/wa' && !isCompleted && (
            <div className="telegramOrWhatsappBtns">
              <Button
                large
                onClick={handleClickTelegramOrWhatsapp(
                  missionExternalUrls?.[0]
                )}
                secondary
                wide
              >
                {t('tooltip.telegram')}
              </Button>
              <Button
                large
                onClick={handleClickTelegramOrWhatsapp(
                  missionExternalUrls?.[1]
                )}
                primary
                wide
              >
                {t('tooltip.whatsApp')}
              </Button>
            </div>
          )}
        </div>
      </ScTooltip>
    </MobileTooltip>
  );
};

const MobileTooltip: React.FC<PropsWithChildren> = ({ children }) => {
  useScrollBlock(false, false);

  const Container = <ScTooltipMobile>{children}</ScTooltipMobile>;
  const portal = createPortal(Container, document.body);

  return portal;
};
